import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ApiService, User } from '@beautyapp/core';
import { BirthdayBadgeComponent, Customer360TriggerDirective } from '@beautyapp/customer-360';

/**
 * Row shape returned by `GET /booking/patients-v2`. Mirrors the
 * backend `PatientListItemV2Dto`. Kept local (not re-exported from
 * `@beautyapp/core`) so v1 types remain untouched.
 */
export interface PatientListRowV2 {
  id: string;
  userId: string;
  userFullName: string | null;
  userIdNumber: string | null;
  userMobile: string | null;
  /**
   * `YYYY-MM-DD`. Optional because it arrives only from beautyapp-back PR #474
   * onwards — until that deploys the field is simply absent and the badge
   * renders nothing, which is the correct degradation rather than an error.
   */
  userBirthDate?: string | null;
  employeeId: string;
  employeeFullName: string | null;
  serviceId: string;
  serviceName: string | null;
  bookingDate: string | null;
  startTime: string;
  endTime: string;
  status: number;
  lastVisitDate: string | null;
  // Projected from `booking.user` on the backend so the card UI can
  // render real numbers instead of zeros after selection.
  isVerified: boolean | null;
  balance: number | null;
  debt: number | null;
  minutes: number | null;
  cashback: number | null;
  cardNo: string | null;
}

interface PaginatedV2 {
  data: PatientListRowV2[];
  meta: { page: number; pageSize: number; itemCount: number; pageCount: number };
}

/**
 * Clone of `UserSelectionModalComponent` that:
 *  1. Calls the new `/booking/patients-v2` endpoint so each row is
 *     enriched with `lastVisitDate` (LATERAL subquery on the backend).
 *  2. Shows the last visit date inline beside each customer name
 *     ("ბოლო ვიზიტი: YYYY-MM-DD" for returning customers,
 *     "ახალი მომხმარებელი" for first-timers).
 *  3. Attaches `Customer360TriggerDirective` to a per-row button so
 *     staff can open the full 360 drawer without leaving the modal.
 *
 * v1 is left untouched per the project rule "never modify existing old
 * code" — consumers that need the 360 integration simply import
 * `UserSelectionModalV2Component` instead of the v1 version.
 */
@Component({
  selector: 'beautyapp-user-selection-modal-v2',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzSpinModule,
    NzEmptyModule,
    NzTagModule,
    NzAvatarModule,
    NzDividerModule,
    NzToolTipModule,
    NzModalModule,
    TranslatePipe,
    Customer360TriggerDirective,
    BirthdayBadgeComponent,
  ],
  template: `
    <div class="user-selection-modal-v2">
      <div class="search-section">
        <nz-input-group [nzPrefix]="searchIcon" [nzSuffix]="suffixIconButton">
          <input
            nz-input
            [(ngModel)]="searchQuery"
            (ngModelChange)="onSearchChange($event)"
            [placeholder]="'users.search.placeholder' | translate"
            (keyup.enter)="performSearch()"
          />
        </nz-input-group>
        <ng-template #searchIcon><i nz-icon nzType="search"></i></ng-template>
        <ng-template #suffixIconButton>
          @if (searchQuery) {
            <button nz-button nzType="text" nzSize="small" (click)="clearSearch()">
              <i nz-icon nzType="close"></i>
            </button>
          }
        </ng-template>
      </div>

      <nz-divider></nz-divider>

      <div class="results-section">
        <nz-spin [nzSpinning]="loading()">
          <div class="results-header">
            <h4>
              <i nz-icon nzType="team" nzTheme="outline"></i>
              {{ 'users.search.results' | translate }}
              @if (rows().length > 0) {
                <span class="results-count">({{ rows().length }})</span>
              }
            </h4>
          </div>

          @if (rows().length > 0) {
            <div class="users-list">
              @for (row of rows(); track row.id) {
                <div class="user-item" (click)="select(row)">
                  <div class="user-avatar">
                    <nz-avatar [nzSize]="48" nzIcon="user"></nz-avatar>
                  </div>
                  <div class="user-info">
                    <div class="user-name">
                      <span>{{ row.userFullName || '—' }}</span>
                      <beautyapp-birthday-badge
                        [birthDate]="row.userBirthDate ?? null"
                      ></beautyapp-birthday-badge>
                      @if (row.lastVisitDate) {
                        <nz-tag nzColor="blue" class="visit-tag">
                          <i nz-icon nzType="history"></i>
                          ბოლო ვიზიტი: {{ row.lastVisitDate }}
                        </nz-tag>
                      } @else {
                        <nz-tag nzColor="green" class="visit-tag">
                          <i nz-icon nzType="star"></i>
                          ახალი მომხმარებელი
                        </nz-tag>
                      }
                    </div>
                    <div class="user-contact">
                      <span class="phone">
                        <i nz-icon nzType="phone" nzTheme="outline"></i>
                        {{ row.userMobile || 'N/A' }}
                      </span>
                      @if (row.userIdNumber) {
                        <span class="id">
                          <i nz-icon nzType="idcard" nzTheme="outline"></i>
                          {{ row.userIdNumber }}
                        </span>
                      }
                    </div>
                  </div>
                  <div class="user-actions">
                    <!-- Customer 360 trigger: no modifications to any existing
                         template — the directive is a standalone attribute
                         applied only inside this new v2 component. -->
                    <button
                      nz-button
                      nzType="default"
                      nz-tooltip="მომხმარებლის პროფილი"
                      [beautyappCustomer360Trigger]="row.userId"
                      [customer360Name]="row.userFullName || ''"
                      [customer360Mobile]="row.userMobile || ''"
                    >
                      <i nz-icon nzType="user" nzTheme="outline"></i>
                    </button>
                    <button
                      nz-button
                      nzType="primary"
                      (click)="select(row); $event.stopPropagation()"
                    >
                      <i nz-icon nzType="check" nzTheme="outline"></i>
                      {{ 'users.choose' | translate }}
                    </button>
                  </div>
                </div>
              }
            </div>
          } @else {
            <div class="empty-state">
              <nz-empty [nzNotFoundImage]="'simple'" [nzNotFoundContent]="emptyContent"></nz-empty>
              <ng-template #emptyContent>
                @if (!hasSearched) {
                  <span>{{ 'users.search.startTyping' | translate }}</span>
                }
                @if (hasSearched && !loading()) {
                  <span>{{ 'users.search.noResults' | translate }}</span>
                }
              </ng-template>
            </div>
          }
        </nz-spin>
      </div>

      <div class="modal-actions">
        <button nz-button nzType="default" (click)="cancel()">
          {{ 'buttons.close' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .user-selection-modal-v2 {
      .search-section nz-input-group .ant-input { font-size: 16px; padding: 12px; }
      .results-section { min-height: 400px; max-height: 600px; }
      .results-header h4 {
        display: flex; align-items: center; gap: 8px; margin: 0;
        font-size: 16px; font-weight: 600; color: #262626;
      }
      .results-header h4 i { color: #1890ff; }
      .results-count { color: #8c8c8c; font-weight: 400; font-size: 14px; }
      .users-list { max-height: 500px; overflow-y: auto; padding-top: 16px; }
      .user-item {
        display: flex; align-items: flex-start; gap: 16px; padding: 16px;
        border: 1px solid #f0f0f0; border-radius: 8px; margin-bottom: 12px;
        cursor: pointer; transition: all 0.2s ease;
      }
      .user-item:hover {
        border-color: #1890ff; background-color: #f6ffed;
        transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      }
      .user-info { flex: 1; min-width: 0; }
      .user-name { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; flex-wrap: wrap; }
      .user-name span { font-size: 16px; font-weight: 500; color: #262626; }
      .visit-tag { font-size: 12px; }
      .user-contact { display: flex; gap: 12px; font-size: 14px; color: #595959; }
      .user-contact i { color: #8c8c8c; font-size: 12px; margin-right: 4px; }
      .user-actions { flex-shrink: 0; display: flex; gap: 8px; align-items: center; }
      .empty-state {
        display: flex; align-items: center; justify-content: center; min-height: 300px;
      }
      .modal-actions {
        display: flex; justify-content: flex-end; margin-top: 24px;
        padding-top: 16px; border-top: 1px solid #f0f0f0;
      }
    }
  `],
})
export class UserSelectionModalV2Component implements OnInit {
  private modal = inject(NzModalRef);
  private api = inject(ApiService);

  loading = signal(false);
  rows = signal<PatientListRowV2[]>([]);
  searchQuery = '';
  hasSearched = false;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        if (term.trim()) {
          this.performSearch();
        } else {
          this.rows.set([]);
          this.hasSearched = false;
        }
      });
  }

  onSearchChange(value: string): void {
    this.searchSubject.next(value);
  }

  performSearch(): void {
    if (!this.searchQuery.trim()) {
      this.rows.set([]);
      this.hasSearched = false;
      return;
    }
    this.loading.set(true);
    this.hasSearched = true;
    this.api
      .get<PaginatedV2>('booking/patients-v2', {
        q: this.searchQuery.trim(),
        page: 1,
        pageSize: 20,
      })
      .subscribe({
        next: (res: PaginatedV2) => {
          this.rows.set(this.dedupeByUserData(res?.data ?? []));
          this.loading.set(false);
        },
        error: () => {
          this.rows.set([]);
          this.loading.set(false);
        },
      });
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.rows.set([]);
    this.hasSearched = false;
  }

  /**
   * Map a v2 patient row back to the `User` shape that downstream
   * consumers (the calendar's `user-card.component`, `VisitAddFacade.
   * saveUser`) expect. Only the fields those consumers actually read
   * are populated — everything else is safely `undefined` because the
   * save flow refetches the full user from the backend right after.
   *
   * `row.userId` (NOT `row.id`) is the customer id. `row.id` is the
   * booking id — confusing them would send the wrong primary key to
   * `updateVisitUser`, so the mapping is deliberate.
   */
  /**
   * Map the raw API response into de-duplicated user rows.
   *
   * The `patients-v2` endpoint returns one row per *booking* rather than
   * one row per *user*. When a customer with the same phone number appears
   * in multiple past appointments (e.g. 555589007 → 3 bookings), we must
   * collapse those rows into a single list entry keyed by `userId`,
   * otherwise the modal shows duplicate names and switching between them
   * fails (beaut-449).
   *
   * We keep the FIRST row for each userId (which already carries
   * balance/debt/isVerified projected from `booking.user`), and surface the
   * most-recent `lastVisitDate` as the visit-tag.
   */
  private dedupeByUserData(raw: PatientListRowV2[]): PatientListRowV2[] {
    const byUser = new Map<string, PatientListRowV2>();
    for (const row of raw) {
      const key = row.userId ?? row.id;
      const existing = byUser.get(key);
      if (!existing) {
        byUser.set(key, { ...row });
      } else {
        // Prefer the most recent visit date if present
        if (row.lastVisitDate && (!existing.lastVisitDate || row.lastVisitDate > existing.lastVisitDate)) {
          existing.lastVisitDate = row.lastVisitDate;
        }
      }
    }
    return Array.from(byUser.values());
  }

  private rowToUser(row: PatientListRowV2): User {
    return {
      id: row.userId,
      fullName: row.userFullName ?? '',
      mobilePhone: row.userMobile ?? '',
      identityNumber: row.userIdNumber ?? '',
      cardNo: row.cardNo ?? '',
      balance: row.balance ?? 0,
      debt: row.debt ?? 0,
      minutes: row.minutes ?? 0,
      cashback: row.cashback ?? 0,
      isVerified: row.isVerified ?? false,
      // Carried through so the card that receives this selection can show the
      // birthday marker. `userBirthDate` is `YYYY-MM-DD` straight from
      // `to_char` — a calendar day, not an instant, which is the only form the
      // badge accepts.
      birthDate: row.userBirthDate ?? null,
    } as unknown as User;
  }

  select(row: PatientListRowV2): void {
    this.modal.close(this.rowToUser(row));
  }

  cancel(): void {
    this.modal.close(null);
  }
}
